import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { forgotPasswordConfirm } from "../../lib/api";

// Opened from the one-time link in the password reset e-mail
// (TP-SAF-WEB-TS-07-TC-0002). Public: the user is not signed in. The token
// is only ever sent to /auth/forgot-password/confirm, which checks that it is
// unused, unexpired and the latest link for its account.
export default function ResetPasswordPage() {
  const location = useLocation();
  const token = useMemo(
    () => new URLSearchParams(location.search).get("token") || "",
    [location.search],
  );
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState(null); // null | "loading" | "done"
  const [error, setError] = useState(null);

  const mismatch = confirm.length > 0 && password !== confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token || !password || password !== confirm) return;
    setStatus("loading");
    setError(null);
    try {
      await forgotPasswordConfirm(token, password);
      setStatus("done");
    } catch (err) {
      setError(err.message || "Failed to reset password. Please try again.");
      setStatus(null);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="h-5 w-5 text-[#08148C]" />
          <h1 className="text-base font-semibold text-slate-900">Set a new password</h1>
        </div>

        {!token && (
          <Alert variant="destructive">
            <AlertDescription>
              This page needs the link from your password reset e-mail. Request a new link from the sign-in page.
            </AlertDescription>
          </Alert>
        )}

        {token && status === "done" && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
            Password has been reset successfully. Please sign in.
          </div>
        )}

        {token && status !== "done" && (
          <form onSubmit={onSubmit} noValidate className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="reset-new-password" className="text-sm font-medium text-slate-700">
                New password <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reset-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="reset-confirm-password" className="text-sm font-medium text-slate-700">
                Confirm new password <span className="text-red-500">*</span>
              </Label>
              <Input
                id="reset-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {mismatch && <p className="text-xs text-red-600">Passwords do not match</p>}
            </div>
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <button
              type="submit"
              disabled={status === "loading" || !password || password !== confirm}
              className="w-full flex items-center justify-center gap-2 bg-[#08148C] hover:bg-[#060f6e] disabled:opacity-60 text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
            >
              {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Reset Password
            </button>
          </form>
        )}

        <div className="mt-4 text-center">
          <Link to="/login" className="text-sm text-[#08148C] hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
